import React, { useContext, useState } from 'react';
import { ListItem, useTheme } from '@mui/material';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ModalProps } from 'shared/lib/components/modals/modal';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { FolderEditParams } from 'widgets/folders/editFolderModal';
import IconCross from 'shared/icons/cross';
import IconOk from 'shared/icons/iconOk';
import { MergeInitialPlacement } from 'entities/types';

export default observer(function CreateFolder({
  handleClose,
  name,
  onSubmit,
  headerText,
  submitText,
}: {
  mergePlacementParams?: MergeInitialPlacement;
  name: string;
  onSubmit: (params: FolderEditParams) => Promise<any>;
  headerText?: string;
  submitText?: string;
} & ModalProps): JSX.Element {
  const { t } = useTranslation();

  headerText = headerText || t('common.settings');
  submitText = submitText || t('common.save');

  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object({
    folderName: yup.string().required(t('errors.validationSchema.required')),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      folderName: name,
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (params) => {
      setIsLoading(true);

      try {
        await onSubmit({
          name: params.folderName,
        });
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    },
  });

  return (
    <ListItem
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        gap: sp.s40,
        alignItems: 'center',
        borderBottom: ` 1px solid ${theme.palette.primary.main}`,
        bgcolor: theme.palette.background.default,
        pl: sp.s25,
        py: 0,
      }}
    >
      <CustomTextField
        wrapperSxProps={{ width: 1 }}
        id="folderName"
        name="folderName"
        value={formik.values.folderName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.folderName && !!formik.errors.folderName}
        helperText={formik.touched.folderName && formik.errors.folderName}
        autoFocus={true}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none',
            },
          },
          '& .MuiInputBase-root': {
            p: 0,
            pl: 0,
          },
          '& .MuiInputBase-input': {
            pl: '2px',
            py: 0,
          },
        }}
      />
      <IconCross
        onClick={handleClose}
        sizeKey="14x14"
        extColor={theme.palette.custom.textInactive}
      />
      <IconOk
        sizeKey="20x20"
        onClick={() => {
          setFormikSubmited(true);
          formik.handleSubmit();
        }}
        extColor={theme.palette.custom.successGreen}
      />
    </ListItem>
  );
});
